import { AlertCircle, ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import type { OperationsMetric, UnavailableOperationsMetric } from "@/api/intelligence";
import { formatCount, formatPercentChange } from "@/lib/intelligenceFormat";
import { cn } from "@/lib/utils";

type Props = {
	metric: OperationsMetric;
	comparisonLabel?: string | null;
};

function formatOpsValue(unit: OperationsMetric["unit"], value: number): string {
	if (unit === "PERCENT") return `${value}%`;
	if (unit === "DAYS") return `${value.toLocaleString("en-IN", { maximumFractionDigits: 1 })} days`;
	return formatCount(value);
}

function TrendIcon({ trend }: { trend: OperationsMetric["trend"] }) {
	if (trend === "up") return <ArrowUpRight className="h-4 w-4 text-emerald-600" />;
	if (trend === "down") return <ArrowDownRight className="h-4 w-4 text-rose-600" />;
	return <Minus className="h-4 w-4 text-muted-foreground" />;
}

export function OperationsMetricCard({ metric, comparisonLabel }: Props) {
	const displayValue = formatOpsValue(metric.unit, metric.value);
	const prevDisplay =
		metric.previousValue !== null ? formatOpsValue(metric.unit, metric.previousValue) : "—";

	return (
		<Card className="overflow-hidden border-slate-200/80 shadow-sm">
			<CardContent className="p-4">
				<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground truncate">
					{metric.label}
				</p>
				{metric.snapshotAsOf && (
					<p className="text-[11px] text-muted-foreground mt-0.5">As of {metric.snapshotAsOf}</p>
				)}
				<p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
					{displayValue}
				</p>
				{metric.percentChange !== null && (
					<div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
						<TrendIcon trend={metric.trend} />
						<span
							className={cn(
								"font-medium tabular-nums",
								metric.trend === "up" && "text-emerald-700",
								metric.trend === "down" && "text-rose-700"
							)}
						>
							{formatPercentChange(metric.percentChange)}
						</span>
						<span className="truncate">vs {comparisonLabel ?? "prior period"}</span>
					</div>
				)}
				{metric.previousValue !== null && (
					<p className="mt-1 text-[11px] text-muted-foreground tabular-nums">
						Previous: {prevDisplay}
					</p>
				)}
			</CardContent>
		</Card>
	);
}

export function UnavailableOperationsCard({ metric }: { metric: UnavailableOperationsMetric }) {
	return (
		<Card className="overflow-hidden border-dashed border-slate-200">
			<CardContent className="p-4">
				<div className="flex items-start gap-2">
					<AlertCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
					<div>
						<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
							{metric.label}
						</p>
						<p className="mt-2 text-lg font-semibold text-muted-foreground">Unavailable</p>
						<p className="mt-1 text-[11px] text-muted-foreground">{metric.reason}</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
